import { EditorContent, useEditor } from '@tiptap/react';
import Blockquote from '@tiptap/extension-blockquote';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import ListItem from '@tiptap/extension-list-item';
import BulletList from '@tiptap/extension-bullet-list';
import Heading from '@tiptap/extension-heading';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Link from '@tiptap/extension-link';
import { useCallback, useEffect, useState } from 'react';
import {
  BlockquoteIco,
  BoldIco,
  BulletListIco,
  ItalicIco,
  SetLinkIco,
  StrikeIco,
  UnderlineIco,
  UnsetLinkIco,
} from './icons';
import { MenuButton } from './MenuButton';
import { Button } from '../Button';
import './styles.css';
import { EMPTY_COMMENTARY_TEXT } from '../../pages/bcp/strategies/StrategyComponents/constants';

interface TiptapEditorProps {
  onFinished: (content: string) => void;
  initialContent?: string;
  buttonalignedRight?: boolean;
}

export default function TiptapEditor({
  onFinished,
  initialContent,
  buttonalignedRight = false,
}: TiptapEditorProps) {
  const [editorContent, setEditorContent] = useState<string>(
    initialContent ? initialContent : '',
  );

  useEffect(() => {
    setEditorContent(initialContent ? initialContent : '');
  }, [initialContent]);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Underline,
      Strike,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Blockquote,
      BulletList,
      ListItem,
      Heading.configure({
        levels: [1, 2, 3, 4, 5],
      }),
    ],
    content: editorContent,
    onUpdate({ editor }) {
      setEditorContent(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    let hrefURL = '';
    const previousUrl = editor!.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor!.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    if (!url.includes('http://')) {
      hrefURL = `http://${url}`;
    }

    // update link
    editor!
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: hrefURL })
      .run();
  }, [editor]);

  useEffect(() => {
    editor?.commands.setContent(
      initialContent ? initialContent : EMPTY_COMMENTARY_TEXT,
    );
  }, [initialContent]);

  if (!editor) {
    return null;
  }
  return (
    <div>
      <div className="flex flex-row flex-wrap justify-between items-center w-full bg-white mb-4">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title={'bold'}
        >
          <BoldIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title={'italic'}
        >
          <ItalicIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title={'underline'}
        >
          <UnderlineIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title={'strike'}
        >
          <StrikeIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          isActive={editor.isActive('heading', { level: 1 })}
          title={'heading'}
        >
          H1
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          isActive={editor.isActive('heading', { level: 2 })}
          title={'heading'}
        >
          H2
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          isActive={editor.isActive('heading', { level: 3 })}
          title={'heading'}
        >
          H3
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
          isActive={editor.isActive('heading', { level: 4 })}
          title={'heading'}
        >
          H4
        </MenuButton>
        <MenuButton
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 5 }).run()
          }
          isActive={editor.isActive('heading', { level: 5 })}
          title={'heading'}
        >
          H5
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title={'blockquote'}
        >
          <BlockquoteIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title={'bulletList'}
        >
          <BulletListIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title={'link'}
        >
          <SetLinkIco className="w-8 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().unsetLink().run()}
          isActive={editor.isActive('link')}
          title={'link'}
        >
          <UnsetLinkIco className="w-8 h-4" />
        </MenuButton>
      </div>
      <div className="editor-container">
        <EditorContent editor={editor} />
      </div>
      <div
        className={`flex items-center mt-6 w-full ${
          buttonalignedRight ? 'justify-end' : ''
        }`}
      >
        <Button
          type="primary"
          className="min-w-[188px] mx-auto h-7"
          htmlType={'submit'}
          onClick={() => onFinished(editorContent)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
